import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { APPLY_FILTER_MEETING_ID_SEARCH_COUNTS_WITH_CONFIG } from '../../graphql/ql/schema';
import { startOfDay, startOfWeek, endOfWeek } from 'date-fns';
import styles from './Mobile.module.css';

interface MobileMeetingListProps {
    onSelectMeeting: (meetingId: string) => void;
    user?: any;
}

const PAGE_SIZE = 30;

type FilterType = 'today' | 'week' | 'mine' | 'all';

function formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '';
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins} min`;
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hrs}h ${remainMins}m`;
}

function formatShortDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function getDateLabel(dateStr: string): string {
    const meetingDate = startOfDay(new Date(dateStr));
    const today = startOfDay(new Date());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (meetingDate.getTime() === today.getTime()) return 'Today';
    if (meetingDate.getTime() === yesterday.getTime()) return 'Yesterday';

    return meetingDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: meetingDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    });
}

function extractMeetings(data: any): any[] {
    return data?.searchDealsWithConfig?.searchResults?.searchResult
        ?.map((r: any) => r.meeting)
        ?.filter(Boolean) || [];
}

function groupByDate(meetings: any[]): { label: string; count: number; meetings: any[] }[] {
    const groups: Map<string, any[]> = new Map();
    for (const meeting of meetings) {
        const label = getDateLabel(meeting.date);
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label)!.push(meeting);
    }
    return Array.from(groups.entries()).map(([label, meetings]) => ({
        label,
        count: meetings.length,
        meetings,
    }));
}

const MobileMeetingList: React.FC<MobileMeetingListProps> = ({ onSelectMeeting, user }) => {
    const [allMeetings, setAllMeetings] = useState<any[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(false);
    const [activeFilter, setActiveFilter] = useState<FilterType>('today');
    const skipRef = useRef(0);
    const loadingRef = useRef(false);
    const hasMoreRef = useRef(true);
    const listRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);

    const [fetchMeetings] = useLazyQuery(
        APPLY_FILTER_MEETING_ID_SEARCH_COUNTS_WITH_CONFIG,
        { fetchPolicy: 'network-only' }
    );

    const getFilterParams = useCallback((filter: FilterType) => {
        const params: any = {};
        const now = new Date();
        if (filter === 'today') {
            params.dateMin = startOfDay(now);
        } else if (filter === 'week') {
            params.dateMin = startOfWeek(now);
            params.dateMax = endOfWeek(now);
        } else if (filter === 'mine' && user?.email) {
            params.hostEmails = user.email;
        }
        return params;
    }, [user]);

    const fetchPage = useCallback(async (skip: number, filter: FilterType) => {
        const filterParams = getFilterParams(filter);
        const result = await fetchMeetings({
            variables: {
                searchTerm: {
                    ...filterParams,
                    returnTranscripts: false,
                    weeklyCount: false,
                    fullList: true,
                },
                args: {
                    limit: PAGE_SIZE,
                    skip,
                    query: '',
                    sortBy: '',
                    sortDir: '',
                },
            },
        });
        return extractMeetings(result.data);
    }, [fetchMeetings, getFilterParams]);

    const loadPage = useCallback(async (skip: number, filter: FilterType) => {
        if (loadingRef.current || !hasMoreRef.current) return;
        loadingRef.current = true;
        if (skip > 0) setLoadingMore(true);

        try {
            let newMeetings = await fetchPage(skip, filter);

            // Auto-fallback on initial load: Today → This Week → All
            if (skip === 0 && newMeetings.length === 0) {
                if (filter === 'today') {
                    setActiveFilter('week');
                    newMeetings = await fetchPage(0, 'week');
                    filter = 'week';
                }
                if (filter === 'week' && newMeetings.length === 0) {
                    setActiveFilter('all');
                    newMeetings = await fetchPage(0, 'all');
                    filter = 'all';
                }
            }

            if (newMeetings.length < PAGE_SIZE) {
                setHasMore(false);
                hasMoreRef.current = false;
            }

            setAllMeetings(prev => {
                const base = skip === 0 ? [] : prev;
                const existingIds = new Set(base.map((m: any) => m.id));
                const unique = newMeetings.filter((m: any) => !existingIds.has(m.id));
                const merged = [...base, ...unique];
                merged.sort((a: any, b: any) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                return merged;
            });

            skipRef.current = skip + PAGE_SIZE;
        } catch (e) {
            console.error('Failed to load meetings:', e);
            if (skip === 0) setError(true);
        } finally {
            loadingRef.current = false;
            setLoadingMore(false);
            if (skip === 0) setInitialLoading(false);
        }
    }, [fetchPage, getFilterParams]);

    // Initial load
    useEffect(() => {
        loadPage(0, activeFilter);
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const handleFilterChange = useCallback((filter: FilterType) => {
        if (filter === activeFilter) return;
        setActiveFilter(filter);
        setAllMeetings([]);
        setHasMore(true);
        hasMoreRef.current = true;
        skipRef.current = 0;
        loadingRef.current = false;
        setError(false);
        setInitialLoading(true);
        loadPage(0, filter);
    }, [activeFilter, loadPage]);

    // Infinite scroll
    useEffect(() => {
        const sentinel = sentinelRef.current;
        const scrollContainer = listRef.current;
        if (!sentinel || !scrollContainer) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMoreRef.current && !loadingRef.current) {
                    loadPage(skipRef.current, activeFilter);
                }
            },
            {
                root: scrollContainer,
                threshold: 0.1,
                rootMargin: '0px 0px 200px 0px',
            }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadPage, initialLoading, activeFilter]);

    const dateGroups = useMemo(() => groupByDate(allMeetings), [allMeetings]);

    const filters: { key: FilterType; label: string }[] = [
        { key: 'today', label: 'Today' },
        { key: 'week', label: 'This Week' },
        { key: 'mine', label: 'My Calls' },
        { key: 'all', label: 'All' },
    ];

    return (
        <>
            <div className={styles.filterChips}>
                {filters.map(f => (
                    <button
                        key={f.key}
                        className={`${styles.filterChip} ${activeFilter === f.key ? styles.filterChipActive : ''}`}
                        onClick={() => handleFilterChange(f.key)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {initialLoading ? (
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner} />
                    <span>Loading meetings...</span>
                </div>
            ) : error && allMeetings.length === 0 ? (
                <div className={styles.errorText}>
                    Unable to load meetings. Please try again.
                </div>
            ) : allMeetings.length === 0 ? (
                <div className={styles.emptyState}>
                    <div className={styles.emptyStateText}>No meetings found</div>
                </div>
            ) : (
                <div className={styles.meetingList} ref={listRef}>
                    {dateGroups.map(group => (
                        <React.Fragment key={group.label}>
                            <div className={styles.dateHeader}>
                                <span className={styles.dateHeaderLabel}>{group.label}</span>
                                <span className={styles.dateHeaderCount}>{group.count} {group.count === 1 ? 'meeting' : 'meetings'}</span>
                            </div>
                            {group.meetings.map((meeting: any) => {
                                const isVideo = meeting.callType !== 'audio';
                                return (
                                    <div
                                        key={meeting.id}
                                        className={styles.meetingItem}
                                        onClick={() => onSelectMeeting(meeting.id)}
                                    >
                                        <div className={`${styles.meetingIcon} ${isVideo ? styles.meetingIconVideo : ''}`}>
                                            {isVideo ? '🎥' : '🎙'}
                                        </div>
                                        <div className={styles.meetingInfo}>
                                            <div className={styles.meetingTitle}>{meeting.title || 'Untitled Meeting'}</div>
                                            <div className={styles.meetingMeta}>
                                                {formatShortDate(meeting.date)}
                                                {meeting.hostName ? ` · ${meeting.hostName}` : ''}
                                            </div>
                                        </div>
                                        <div className={styles.meetingDuration}>
                                            {formatDuration(meeting.duration)}
                                        </div>
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}

                    <div
                        ref={sentinelRef}
                        style={{
                            height: 60,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {loadingMore && <div className={styles.spinner} />}
                        {!hasMore && allMeetings.length > 0 && (
                            <span style={{ color: '#9ca3af', fontSize: 13 }}>No more meetings</span>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default MobileMeetingList;
